/**
 * Timeline component - used for Change History visualization.
 * Adapted from reference Timeline design with central axis, alternating cards, old/new value comparison.
 */
import * as React from 'react';
import { useEffect, useState, useRef, useCallback } from 'react';
import { Box } from '@mui/material';

const DEFAULT_VISIBLE_COUNT = 3;

const ORANGE = '#F58220';
const ORANGE_DARK = '#C45A1C';
const DOT_COLOR = '#BCBCBC';
const GREY_RING = '#C8C8C8';
const TITLE_COLOR = '#333333';
const ITEM_TITLE_COLOR = ORANGE;
const BODY_COLOR = '#777777';
const BADGE_USER_BG = '#E3F2FD';
const BADGE_USER_COLOR = '#1565C0';
const BADGE_DATE_BG = '#E0F7FA';
const BADGE_DATE_COLOR = '#00695C';
const BADGE_SYSTEM_BG = '#F3E5F5';
const BADGE_SYSTEM_COLOR = '#6A1B9A';
const CARD_BORDER = '#e5e5e5';

const NODE_OUTER_RING = 32;
const NODE_OUTER_BORDER = 1.5;
const NODE_INNER_RING = 22;
const NODE_INNER_BORDER = 3;
const NODE_CENTER_DOT = 8;

const CONNECTOR_HEIGHT = 3;

const DOT_SIZE = 5;
const DOT_PAIR_SPREAD = 22;
const DOT_SPACING = 20;

const COMPARISON_MAX_HEIGHT = 120;

export interface TimelineEvent {
  userId?: string;
  userName: string;
  created: string;
  entity?: string;
  entityId?: string;
  entityGuid?: string | null;
  fieldChanged: string;
  oldValue: string;
  newValue: string;
  externalSystem: string;
}

export interface TimelineProps {
  data: TimelineEvent[];
  visibleCount?: number;
  /** When true, renders for modal (no minHeight 100vh, compact layout) */
  embedded?: boolean;
}

function IconUser({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginRight: 4, flexShrink: 0 }}
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function IconCalendar({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginRight: 4, flexShrink: 0 }}
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function IconMonitor({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginRight: 4, flexShrink: 0 }}
    >
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function IconChevronUp({ size = 22 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}

function IconChevronDown({ size = 22 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function TimelineCard({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <Box
      sx={{
        backgroundColor: 'white',
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: '4px',
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          borderColor: '#bbb',
        },
        ...style,
      }}
    >
      {children}
    </Box>
  );
}

function TimelineCardMobile({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        backgroundColor: 'white',
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: '4px',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          borderColor: '#bbb',
        },
      }}
    >
      {children}
    </Box>
  );
}

const BADGE_VARIANTS = {
  user: { bg: BADGE_USER_BG, color: BADGE_USER_COLOR },
  date: { bg: BADGE_DATE_BG, color: BADGE_DATE_COLOR },
  system: { bg: BADGE_SYSTEM_BG, color: BADGE_SYSTEM_COLOR },
} as const;

function TimelineBadge({
  children,
  testId,
  variant = 'user',
}: {
  children: React.ReactNode;
  testId?: string;
  variant?: 'user' | 'date' | 'system';
}) {
  const { bg, color } = BADGE_VARIANTS[variant];
  return (
    <span
      data-testid={testId}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: bg,
        color,
        fontSize: '11px',
        fontFamily: "'Inter', sans-serif",
        padding: '2px 8px',
        borderRadius: '9999px',
        whiteSpace: 'nowrap',
        lineHeight: 1.6,
      }}
    >
      {children}
    </span>
  );
}

function parseLines(value: string): string[] {
  if (!value || value === '--') return [value || '--'];
  return value
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function ValueListItems({ list }: { list: string[] }) {
  return (
    <>
      {list.map((item, idx) => (
        <div
          key={idx}
          style={{
            display: 'block',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            fontSize: '12px',
            lineHeight: 1.7,
            color: BODY_COLOR,
            fontFamily: "'Inter', sans-serif",
          }}
          dangerouslySetInnerHTML={{ __html: item }}
        />
      ))}
    </>
  );
}

function ValueComparison({
  oldValue,
  newValue,
}: {
  oldValue: string;
  newValue: string;
}) {
  const oldLines = parseLines(oldValue);
  const newLines = parseLines(newValue);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      setIsOverflowing(el.scrollHeight > COMPARISON_MAX_HEIGHT);
    }
  }, [oldValue, newValue]);

  return (
    <div>
      <div
        ref={contentRef}
        style={{
          maxHeight: expanded ? 'none' : `${COMPARISON_MAX_HEIGHT}px`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
          <div style={{ paddingRight: '6px', minWidth: 0 }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: '13px',
                color: TITLE_COLOR,
                fontFamily: "'Inter', sans-serif",
                paddingBottom: '4px',
              }}
            >
              Old Value
            </div>
            <div
              style={{
                height: '1px',
                backgroundColor: '#ddd',
                marginBottom: '6px',
              }}
            />
            <ValueListItems list={oldLines} />
          </div>
          <div style={{ paddingLeft: '6px', borderLeft: '1px solid #eee', minWidth: 0 }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: '13px',
                color: TITLE_COLOR,
                fontFamily: "'Inter', sans-serif",
                paddingBottom: '4px',
              }}
            >
              New Value
            </div>
            <div
              style={{
                height: '1px',
                backgroundColor: '#ddd',
                marginBottom: '6px',
              }}
            />
            <ValueListItems list={newLines} />
          </div>
        </div>
        {!expanded && isOverflowing && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '40px',
              background: 'linear-gradient(transparent, white)',
              pointerEvents: 'none',
            }}
          />
        )}
      </div>
      {isOverflowing && (
        <button
          data-testid="button-toggle-comparison"
          onClick={() => setExpanded(!expanded)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginTop: '6px',
            padding: 0,
            border: 'none',
            background: 'none',
            color: ORANGE,
            fontSize: '12px',
            fontWeight: 600,
            fontFamily: "'Inter', sans-serif",
            cursor: 'pointer',
          }}
        >
          {expanded ? (
            <>
              <IconChevronUp size={14} />
              Show less
            </>
          ) : (
            <>
              <IconChevronDown size={14} />
              Show more
            </>
          )}
        </button>
      )}
    </div>
  );
}

function MetadataBadges({ event }: { event: TimelineEvent }) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginTop: '10px',
      }}
    >
      <TimelineBadge testId={`badge-user-${event.created}`} variant="user">
        <IconUser />
        {event.userName || '—'}
      </TimelineBadge>
      <TimelineBadge testId={`badge-date-${event.created}`} variant="date">
        <IconCalendar />
        {formatDate(event.created)}
      </TimelineBadge>
      <TimelineBadge testId={`badge-system-${event.created}`} variant="system">
        <IconMonitor />
        {event.externalSystem || '—'}
      </TimelineBadge>
    </div>
  );
}

function CenterNodeStatic({ color }: { color: string }) {
  return (
    <div
      style={{
        width: `${NODE_OUTER_RING}px`,
        height: `${NODE_OUTER_RING}px`,
        borderRadius: '50%',
        border: `${NODE_OUTER_BORDER}px solid ${GREY_RING}`,
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: `${NODE_INNER_RING}px`,
          height: `${NODE_INNER_RING}px`,
          borderRadius: '50%',
          border: `${NODE_INNER_BORDER}px solid ${color}`,
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: `${NODE_CENTER_DOT}px`,
            height: `${NODE_CENTER_DOT}px`,
            borderRadius: '50%',
            backgroundColor: color,
          }}
        />
      </div>
    </div>
  );
}

function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return width;
}

function DesktopTimelineEntry({
  event,
  index,
}: {
  event: TimelineEvent;
  index: number;
}) {
  const isLeft = index % 2 === 0;
  const connectorTop = NODE_OUTER_RING / 2 - CONNECTOR_HEIGHT / 2;

  const card = (
    <TimelineCard>
      <div style={{ padding: '16px' }}>
        <h3
          data-testid={`timeline-title-${index}`}
          style={{
            fontSize: '15px',
            fontWeight: 700,
            color: ITEM_TITLE_COLOR,
            marginBottom: '8px',
            fontFamily: "'Inter', sans-serif",
            lineHeight: 1.4,
          }}
        >
          {event.fieldChanged}
        </h3>
        <ValueComparison oldValue={event.oldValue} newValue={event.newValue} />
        <MetadataBadges event={event} />
      </div>
    </TimelineCard>
  );

  return (
    <div
      data-testid={`timeline-item-${index}`}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        width: '100%',
      }}
    >
      <div style={{ paddingTop: `${connectorTop}px` }}>
        {isLeft && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    height: `${CONNECTOR_HEIGHT}px`,
                    backgroundColor: ORANGE,
                  }}
                />
                {card}
              </div>
              <div style={{ width: '10px', paddingTop: 0 }}>
                <div
                  style={{
                    height: `${CONNECTOR_HEIGHT}px`,
                    backgroundColor: ORANGE,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 10,
        }}
      >
        <CenterNodeStatic color={ORANGE} />
      </div>

      <div style={{ paddingTop: `${connectorTop}px` }}>
        {!isLeft && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              <div style={{ width: '10px', paddingTop: 0 }}>
                <div
                  style={{
                    height: `${CONNECTOR_HEIGHT}px`,
                    backgroundColor: ORANGE,
                  }}
                />
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    height: `${CONNECTOR_HEIGHT}px`,
                    backgroundColor: ORANGE,
                  }}
                />
                {card}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MobileTimelineEntry({
  event,
  index,
}: {
  event: TimelineEvent;
  index: number;
}) {
  return (
    <div
      data-testid={`timeline-item-${index}`}
      style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <CenterNodeStatic color={ORANGE} />
        <div
          style={{
            width: `${CONNECTOR_HEIGHT}px`,
            flexGrow: 1,
            minHeight: '20px',
            backgroundColor: ORANGE,
          }}
        />
      </div>
      <div
        style={{
          paddingTop: '2px',
          paddingBottom: '16px',
          minWidth: 0,
          flex: 1,
        }}
      >
        <TimelineCardMobile>
          <div style={{ padding: '12px' }}>
            <h3
              data-testid={`timeline-title-${index}`}
              style={{
                fontSize: '14px',
                fontWeight: 700,
                color: ITEM_TITLE_COLOR,
                marginBottom: '8px',
                fontFamily: "'Inter', sans-serif",
                lineHeight: 1.4,
              }}
            >
              {event.fieldChanged}
            </h3>
            <ValueComparison oldValue={event.oldValue} newValue={event.newValue} />
            <MetadataBadges event={event} />
          </div>
        </TimelineCardMobile>
      </div>
    </div>
  );
}

function NavArrowButton({
  direction,
  onClick,
  disabled,
}: {
  direction: 'up' | 'down';
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      data-testid={`button-timeline-${direction}`}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: `2px solid ${disabled ? '#ddd' : ORANGE}`,
        backgroundColor: disabled ? '#f5f5f5' : 'white',
        color: disabled ? '#ccc' : ORANGE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.25s ease',
        zIndex: 2,
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={e => {
        if (!disabled) {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
          (e.currentTarget as HTMLButtonElement).style.color = 'white';
        }
      }}
      onMouseLeave={e => {
        if (!disabled) {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'white';
          (e.currentTarget as HTMLButtonElement).style.color = ORANGE;
        }
      }}
    >
      {direction === 'up' ? <IconChevronUp /> : <IconChevronDown />}
    </button>
  );
}

export default function Timeline({
  data,
  visibleCount = DEFAULT_VISIBLE_COUNT,
  embedded = false,
}: TimelineProps) {
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;

  const sortedData = React.useMemo(
    () =>
      [...data].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      ),
    [data]
  );

  const totalItems = sortedData.length;
  const hasNavigation = totalItems > visibleCount;

  const [windowStart, setWindowStart] = useState(0);
  const [animationDirection, setAnimationDirection] = useState<
    'up' | 'down' | null
  >(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number | null>(null);

  const maxStart = Math.max(0, totalItems - visibleCount);
  const visibleItems = sortedData.slice(
    windowStart,
    windowStart + visibleCount
  );

  const canGoUp = windowStart > 0;
  const canGoDown = windowStart < maxStart;

  const navigate = useCallback(
    (dir: 'up' | 'down') => {
      if (isTransitioning) return;
      if (dir === 'up' && !canGoUp) return;
      if (dir === 'down' && !canGoDown) return;

      setAnimationDirection(dir);
      setIsTransitioning(true);

      setTimeout(() => {
        setWindowStart(prev =>
          dir === 'up'
            ? Math.max(0, prev - 1)
            : Math.min(maxStart, prev + 1)
        );
        setAnimationDirection(null);
        setTimeout(() => setIsTransitioning(false), 50);
      }, 280);
    },
    [canGoUp, canGoDown, maxStart, isTransitioning]
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartY.current === null) return;
      const diff = touchStartY.current - e.changedTouches[0].clientY;
      touchStartY.current = null;
      if (Math.abs(diff) > 50) {
        navigate(diff > 0 ? 'down' : 'up');
      }
    },
    [navigate]
  );

  const containerStyle: React.CSSProperties = embedded
    ? {
        minHeight: '100%',
        height: '100%',
        width: '100%',
        backgroundColor: 'white',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
      }
    : {
        minHeight: '100vh',
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: '30px',
        paddingBottom: '30px',
      };

  if (sortedData.length === 0) {
    return (
      <div
        style={{
          ...containerStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <p
          style={{
            color: BODY_COLOR,
            fontFamily: "'Inter', sans-serif",
            fontSize: '14px',
          }}
        >
          No timeline events found.
        </p>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div
        style={{
          ...containerStyle,
          padding: embedded ? '16px 12px' : '24px 12px',
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div style={{ maxWidth: '500px', margin: '0 auto' }}>
          {hasNavigation && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '16px',
              }}
            >
              <NavArrowButton
                direction="up"
                onClick={() => navigate('up')}
                disabled={!canGoUp}
              />
            </div>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '16px',
            }}
          >
            <CenterNodeStatic color={ORANGE} />
          </div>
          <div style={{ overflow: 'hidden' }}>
            <div
              style={{
                transition: 'opacity 0.3s ease, transform 0.3s ease',
                opacity: animationDirection ? 0 : 1,
                transform:
                  animationDirection === 'up'
                    ? 'translateY(30px)'
                    : animationDirection === 'down'
                      ? 'translateY(-30px)'
                      : 'translateY(0)',
              }}
            >
              {visibleItems.map((event, i) => (
                <MobileTimelineEntry
                  key={`${windowStart}-${i}`}
                  event={event}
                  index={windowStart + i}
                />
              ))}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '8px',
            }}
          >
            <CenterNodeStatic color={ORANGE_DARK} />
          </div>
          {hasNavigation && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '16px',
              }}
            >
              <NavArrowButton
                direction="down"
                onClick={() => navigate('down')}
                disabled={!canGoDown}
              />
            </div>
          )}
          {hasNavigation && (
            <div
              style={{
                textAlign: 'center',
                marginTop: '10px',
                fontSize: '12px',
                color: BODY_COLOR,
                fontFamily: "'Inter', sans-serif",
              }}
              data-testid="text-timeline-counter"
            >
              {windowStart + 1}–{Math.min(windowStart + visibleCount, totalItems)}{' '}
              of {totalItems}
            </div>
          )}
        </div>
      </div>
    );
  }

  const innerWrapperStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '1100px',
    margin: '0 16px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    ...(embedded && { flex: 1, minHeight: 0 }),
  };

  return (
    <div
      style={containerStyle}
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div style={innerWrapperStyle}>
        {hasNavigation && (
          <div
            style={{
              position: 'absolute',
              top: '10px',
              right: 0,
              fontSize: '12px',
              color: BODY_COLOR,
              fontFamily: "'Inter', sans-serif",
              zIndex: 20,
            }}
            data-testid="text-timeline-counter"
          >
            {windowStart + 1}–{Math.min(windowStart + visibleCount, totalItems)}{' '}
            of {totalItems}
          </div>
        )}
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: `${DOT_PAIR_SPREAD}px`,
            backgroundImage:
              `radial-gradient(circle ${DOT_SIZE / 2}px at ${DOT_SIZE / 2}px ${DOT_SIZE / 2}px, ${DOT_COLOR} 100%, transparent 0), ` +
              `radial-gradient(circle ${DOT_SIZE / 2}px at ${DOT_PAIR_SPREAD - DOT_SIZE / 2}px ${DOT_SIZE / 2}px, ${DOT_COLOR} 100%, transparent 0)`,
            backgroundSize: `${DOT_PAIR_SPREAD}px ${DOT_SPACING}px`,
            backgroundRepeat: 'repeat-y',
            zIndex: 0,
          }}
        />

        {hasNavigation && (
          <div style={{ zIndex: 2, paddingTop: '10px' }}>
            <NavArrowButton
              direction="up"
              onClick={() => navigate('up')}
              disabled={!canGoUp}
            />
          </div>
        )}

        <div style={{ height: hasNavigation ? '10px' : '40px', zIndex: 1 }} />

        <div style={{ width: '100%', overflow: 'hidden' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              transition: 'opacity 0.3s ease, transform 0.3s ease',
              opacity: animationDirection ? 0 : 1,
              transform:
                animationDirection === 'up'
                  ? 'translateY(30px)'
                  : animationDirection === 'down'
                    ? 'translateY(-30px)'
                    : 'translateY(0)',
            }}
          >
            {visibleItems.map((event, i) => (
              <div key={`${windowStart}-${i}`} style={{ zIndex: 1, width: '100%' }}>
                <DesktopTimelineEntry
                  event={event}
                  index={windowStart + i}
                />
              </div>
            ))}
          </div>
        </div>

        <div style={{ height: hasNavigation ? '10px' : '40px', zIndex: 1 }} />

        {hasNavigation && (
          <div style={{ zIndex: 2, paddingBottom: '10px' }}>
            <NavArrowButton
              direction="down"
              onClick={() => navigate('down')}
              disabled={!canGoDown}
            />
          </div>
        )}
      </div>
    </div>
  );
}
